import { styled } from '@mui/material/styles';
import { InstagramEmbed } from 'react-social-media-embed';

import type { AdventWinner } from '@/competitions/2023/december/pages/LandingPage/schemas.ts';
import ChatButton from '@/components/Chat/_components/ChatAnswers/_components/ChatButton/index.ts';

const ModalContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

const ItemsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  paddingTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.christmasGreenLight,
  width: '100%',
  border: `3px solid ${theme.palette.background.christmasGreenLight}`,
  borderRadius: '30px',
  [theme.breakpoints.up('sm')]: {
    width: '55%',
  },
}));

const ItemRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'space-between',
  justifyContent: 'space-between',
  flexDirection: 'row',
  marginBottom: theme.spacing(1),
  marginRight: theme.spacing(1),
  width: '80%',
}));

const ActionButton = styled(ChatButton)(({ theme }) => ({
  minWidth: 150,
  [theme.breakpoints.up('sm')]: {
    minWidth: 200,
  },
}));

const StyledText = styled('p')(({ theme }) => ({
  fontSize: '19px',
  paddingBottom: theme.spacing(1),
}));

const TextDetails = styled('p')(({ theme }) => ({
  fontSize: '19px',
  color: theme.palette.common.white,
  paddingTop: theme.spacing(1),
}));

const BoldText = styled('p')(({ theme }) => ({
  textAlign: 'center',
  fontSize: '19px',
  fontWeight: 700,
  paddingBottom: theme.spacing(3),
}));

const DayTitleText = styled('p')(({ theme }) => ({
  fontSize: '29px',
  fontWeight: 700,
  color: theme.palette.background.christmasGreen,
  paddingBottom: theme.spacing(2),
}));

const InstagramEmbedContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const getWinnerDetails = (winner: AdventWinner) => [
  { title: 'Kjønn', value: winner.name },
  { title: 'Alder', value: winner.age },
  { title: 'Sted', value: winner.city },
  ...(winner.meta.paidAmount ? [{ title: 'Nedbetalt beløp', value: winner.meta.paidAmount }] : []),
];

interface ModalContentProps {
  onClick(...args: unknown[]): unknown;
  dayTitle: string;
  winners: AdventWinner[];
}

const ModalContent = ({ onClick, dayTitle, winners }: ModalContentProps) => {
  const { amount, remainingAmount, instagramPostUrl } = winners[0].meta;

  return (
    <ModalContainer>
      <StyledText>Dagens vinnere</StyledText>
      <DayTitleText>{dayTitle}</DayTitleText>

      {amount ? (
        <>
          <StyledText>Lukens beløp</StyledText>
          <DayTitleText>{amount}</DayTitleText>
        </>
      ) : null}

      {instagramPostUrl ? (
        <InstagramEmbedContainer>
          <InstagramEmbed url={instagramPostUrl} width={328} />
        </InstagramEmbedContainer>
      ) : null}

      {winners.map((winner, winnerIndex) => (
        <ItemsContainer key={winnerIndex}>
          {getWinnerDetails(winner).map((detail) => (
            <ItemRow key={detail.title}>
              <TextDetails>{detail.title}:</TextDetails>
              <TextDetails>
                <strong>{detail.value}</strong>
              </TextDetails>
            </ItemRow>
          ))}
        </ItemsContainer>
      ))}

      {remainingAmount ? <BoldText>{remainingAmount}</BoldText> : null}

      <ActionButton label="OK" onClick={onClick} />
    </ModalContainer>
  );
};

export default ModalContent;
